import { useEffect, useRef, useState } from 'react';
import { Check, GripVertical, Settings } from 'lucide-react';
import { Skeleton } from '../Skeleton';
import type { FacetDef, FacetValue } from './types';
import { Checkbox } from '../inputs/Checkbox';
import { X } from 'lucide-react';
import { SearchGroup } from './SearchGroup';
import { Tooltip } from '../Tooltip';
import { useFacetVisibility } from './useFacetVisibility';
import { useRailWidth } from './useRailWidth';

/*
  One row shape for every entry in every facet, `All` included.

  The rail speaks in ONE voice: every value is a plain checkbox label in this font, at this
  size, at this weight — status included. The status pill belongs on a record (a table cell,
  a list row, a profile) where it separates a state from body text; in a column of
  checkboxes it made one section read in a different typeface and size from the rest, and
  the `All` row could not match the values it stands in for. Product call, and it overrides
  the "a status is a pill everywhere" line the reference used to carry.
*/
const FACET_ROW =
  'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-600 transition hover:bg-slate-100';


/**
 * The left filter rail — reference §3.
 *
 * Collapsible, hidden by default, state in localStorage. Facet counts come from the
 * server, computed against the current filter set EXCLUDING the facet being counted, so
 * toggling one value never zeroes its own siblings.
 *
 * Every facet leads with `All`, which is the absence of a constraint on that axis rather
 * than a value of it. It is the only way back from a tick, and the reason the rail needs no
 * clear-all control: `All` on each group undoes that group, where the reader is already
 * looking.
 *
 * The rail stays mounted and animates its width so the toggle slides rather than snaps.
 * The animating box owns the width and clips; the panel inside keeps a fixed width, so the
 * content translates out of view instead of reflowing to nothing mid-transition. While
 * closed it is `inert` — off the tab order and out of the accessibility tree — so a
 * collapsed rail is not a set of invisible focus stops.
 *
 * Three things sit on top of that, each with its own state and none of them touching the
 * query:
 *
 * - a **sticky header** carrying the standard `<SearchGroup>`, which narrows which
 *   checkboxes are drawn and never what the list endpoint is asked for;
 * - a **draggable width** (`useRailWidth`), persisted per module, with a standing grip on
 *   the border because a resize edge is not otherwise discoverable;
 * - a **cog listing the module's filter groups** (`useFacetVisibility`), so the axes a
 *   reader never uses can be put away — except one that is currently filtering, which is
 *   disabled with a reason rather than allowed to vanish while still narrowing the list —
 *   and, in its own section, **which axis the folder-tab strip above the table files by**.
 *   The two share a menu because this is the one place in the product that already lists
 *   the module's axes by name, and the second question is asked about the same words.
 */
export function IndexFilterRail({
  module,
  open,
  onClose,
  facets,
  counts,
  selected,
  onSelect,
  onClear,
  navFacet,
  onNavFacetChange,
  loading,
}: {
  module: string;
  open: boolean;
  /**
   * Closes the rail from inside it.
   *
   * <b>The toolbar's filter button is the only other way out</b>, and it is at the far end of a
   * strip the rail has just pushed across — so a reader who opened the rail to tick one facet
   * had to go back and find the control they came from. This is the same close, where they are
   * standing.
   *
   * <b>It is the rail's own chrome, not a filter</b>, which is why it sits OUTSIDE the search
   * group rather than as a third segment of it: the group is search + settings, one control
   * about what is in the rail, and a close button inside it would read as doing something to
   * the search.
   */
  onClose: () => void;
  facets: FacetDef[];
  counts: Record<string, FacetValue[]>;
  selected: Record<string, string[]>;
  /**
   * Replaces one facet's selection outright, leaving every other facet where it is. An
   * empty list is `All` — no constraint on that axis.
   */
  onSelect: (key: string, values: string[]) => void;
  /**
   * Drops the axis, which is different from selecting nothing on it: this says the reader
   * has no opinion and every row qualifies, where `onSelect(key, [])` says they do and it
   * excludes everything.
   */
  onClear: (key: string) => void;
  /** Which facet the folder-tab strip above the table currently files by. */
  navFacet: string;
  /**
   * Repoints that strip. It is an ARRANGEMENT and never a filter: no facet selection moves,
   * so whatever the reader had ticked on the old axis is still ticked, still applied, and
   * still on screen in the rail. A layout control that quietly cleared a filter would be
   * destructive — the same reasoning that stops hiding a group from clearing it.
   */
  /**
   * Repoints the folder nav. **Absent removes the section**, which is what a page supplying
   * `navSlices` does: its strip is a declared taxonomy rather than one axis's values, so
   * there is no axis to choose and a picker here could never change what the strip draws.
   */
  onNavFacetChange?: (key: string) => void;
  loading: boolean;
}) {
  /*
    The rail's own search, and it never reaches the server.

    It narrows WHICH CHECKBOXES ARE DRAWN, nothing else — a Unit facet lists every active org
    unit, and finding one in a column of forty was a scroll rather than a filter. The list
    endpoint is untouched: the reader is looking for a filter, not filtering the list.

    Local state, deliberately. It is a property of looking at the rail rather than of the
    query, so it belongs to neither `useIndexQuery` nor the URL — and nothing about the
    records on screen changes when it is typed into.
  */
  const [search, setSearch] = useState('');

  const term = search.trim().toLowerCase();

  /*
    Value labels only — not the facet's own heading. Matching the heading too would make
    "unit" reveal every unit in the group while "u" revealed most of the rail, and a search
    whose scope the reader cannot predict is worse than one that finds less.
  */
  const matches = (value: FacetValue) => term === '' || value.label.toLowerCase().includes(term);

  const { width, resizing, startResize, reset: resetWidth } = useRailWidth(module);
  const { toggle: toggleFacet, isVisible } = useFacetVisibility(module);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!settingsOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (!settingsRef.current?.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setSettingsOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [settingsOpen]);

  /*
    An axis the reader is filtering on cannot be put away. The stored preference is what they
    asked to hide; this overrules it, so no arrangement of stored state and later clicks can
    narrow the list by something with no control on screen to show it.
  */
  const isConstrained = (facet: FacetDef) => {
    const chosen = selected[facet.key];
    if (chosen === undefined) return false;

    /*
      The resting `status: ['active']` is not a filter, and this has to agree with
      `activeFilterCount` in `useIndexQuery` — the two answer the same question and a reader
      must not meet a toolbar badge saying "unfiltered" beside a rail entry saying "in use".

      It is also the axis with a second visible control that does not move: the index cog's
      Show deleted reads this same state, so putting the rail's group away never makes it
      invisible. The folder nav used to be named here and no longer can be — the reader
      repoints it from the cog below, so it may not be sitting on status at all.
    */
    if (facet.key === 'status' && chosen.length === 1 && chosen[0] === 'active') {
      return false;
    }

    const values = counts[facet.key] ?? [];

    // Every value ticked is the same result set as no constraint — see `unconstrained` below.
    return chosen.length === 0 || !values.every((value) => chosen.includes(value.value));
  };

  const drawn = facets.filter((facet) => isVisible(facet.key, isConstrained(facet)));

  /*
    Only used to decide whether the search found anything. It is deliberately not shown: the
    rail's strip has no count segment, because a rail is only as wide as it is and the number
    was taking the room the search itself needed.
  */
  const shownTotal = drawn.reduce(
    (running, facet) => running + (counts[facet.key] ?? []).filter(matches).length,
    0,
  );

  return (
    <div
      className={[
        'relative shrink-0 overflow-hidden bg-slate-50',
        /*
          The open/close animation is dropped while a drag is running: easing toward the
          pointer over 200ms reads as lag, not as animation.
        */
        resizing ? '' : 'transition-[width] duration-200 ease-out motion-reduce:transition-none',
        open ? 'border-r border-slate-200' : '',
      ].join(' ')}
      style={{ width: open ? width : 0 }}
    >
      <aside
        id={`${module}-filter-rail`}
        aria-label="Filters"
        inert={!open}
        className="modern-scrollbar h-full overflow-y-auto"
        /*
          A FIXED width, not `w-full`. The box above animates from 0 and clips; a rail sized
          to it would reflow its content to nothing mid-transition instead of translating out
          of view.
        */
        style={{ width }}
      >
        {/*
          The sticky header, and it holds the STANDARD search group — the same connected
          strip the index toolbar and every embedded table carry, not a bare input dropped
          above a list. A rail with forty values in it scrolls, and a search that scrolled
          away with them would have to be found again before it could be used.

          Sticky rather than a flex sibling above the scroll region, so there is still one
          scrolling box: the facets pass underneath it. That costs an opaque ground and the
          padding moving off the `<aside>` onto the header and the list separately — a
          sticky child of a padded container sticks below the padding, leaving a gap the
          content shows through.

          `py-2.5` is `<IndexToolbar>`'s, and it has to stay `<IndexToolbar>`'s. The two sit
          side by side at the same top edge, each closing on a `border-b`, so their heights
          are what puts those two borders on one line. Both hold a 40px control, so the
          padding is the whole of it: at `py-3` this header ran 4px deeper and the rule across
          the top of the page visibly stepped down at the rail's edge.
        */}
        <div className="sticky top-0 z-10 flex items-stretch gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5">
          {/*
            THE CLOSE, and it is the rail's own rather than one of the search group's segments.

            `min-w-0` on the group is what keeps it from pushing this off the edge: a flex item
            defaults to `min-width: auto`, so the group would refuse to shrink below its
            content and the button would overflow the rail it is meant to close.
          */}
          <div className="min-w-0 flex-1">
          <SearchGroup
            id={`${module}-facet`}
            value={search}
            onChange={setSearch}
            placeholder="Search filters"
          >
            {/*
              The rail's own cog, built to match the index toolbar's exactly — same segment,
              same panel, same tick. It lists the module's filter groups so a reader can put
              away the axes they never use; a module can carry more than any one reader wants
              in front of them, and the ones they do use then start below the fold.
            */}
            <div className="relative flex shrink-0 items-stretch" ref={settingsRef}>
              <Tooltip label="Filter settings">
                <button
                  type="button"
                  id={`${module}-facet-settings`}
                  onClick={() => setSettingsOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={settingsOpen}
                  aria-label="Filter settings"
                  className="flex items-center rounded-r-lg border-l border-slate-300 px-3 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200"
                >
                  <Settings className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>

              {settingsOpen ? (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-40 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-raised"
                >
                  <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                    Filter groups
                  </p>

                  {facets.map((facet) => {
                    const constrained = isConstrained(facet);
                    const shown = isVisible(facet.key, constrained);

                    return (
                      <Tooltip
                        key={facet.key}
                        /*
                          `block`, or the entries lay out INLINE and wrap into columns — the
                          tooltip anchor is an `inline-flex` span by default, so three groups
                          came out as two on one line and one on the next. A menu is read top
                          to bottom, one row per entry, always.
                        */
                        block
                        /*
                          Disabled for STATE, so it says why — the shell's own rule. This is
                          not a permission gate: a gate hides, and every reader who can see
                          the rail may put a group away. What they may not do is put away a
                          group that is currently narrowing the list.
                        */
                        disabledReason={
                          constrained
                            ? 'This filter is in use. Clear it before putting it away.'
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={shown}
                          id={`${module}-facet-toggle-${facet.key}`}
                          disabled={constrained}
                          onClick={() => toggleFacet(facet.key)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                        >
                          {/* The tick is the only thing saying whether the group is drawn. */}
                          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                            {shown ? (
                              <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                            ) : null}
                          </span>
                          <span className="min-w-0 flex-1 truncate">{facet.label}</span>
                        </button>
                      </Tooltip>
                    );
                  })}

                  {/*
                    Which axis the folder-tab strip above the table files by.

                    It is here rather than in the index cog because this menu already lists
                    the module's axes by name, and the reader choosing one is choosing among
                    the very groups drawn under it — the same words, one section apart. The
                    page still declares the default; this is where a reader disagrees with it.

                    SINGLE choice, so `menuitemradio` rather than the checkbox role the groups
                    above take. One axis files the table, and a menu that let two be ticked
                    would be promising something the strip cannot do.
                  */}
                  {facets.length > 0 && onNavFacetChange ? (
                    <>
                      {/* A section owns the divider above it — the index cog's rule. */}
                      <div className="my-1.5 border-t border-slate-200" role="separator" />

                      <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                        Folder nav
                      </p>

                      {facets.map((facet) => {
                        const chosen = facet.key === navFacet;

                        /*
                          An axis with no values draws no strip at all, so offering it would be
                          a control that silently does nothing. Disabled for STATE with the
                          reason, never hidden — the reader has to be able to see that the axis
                          exists and that there is nothing on it yet. It stays disabled while it
                          is the CHOSEN one, which is the only thing on screen that explains a
                          missing strip.

                          Guarded on `loading`, or every row is disabled for the length of the
                          first fetch: the counts arrive with the rows.
                        */
                        const empty = !loading && (counts[facet.key] ?? []).length === 0;

                        return (
                          <Tooltip
                            key={facet.key}
                            // `block`, or the entries wrap into columns — see above.
                            block
                            disabledReason={
                              empty ? 'This filter has no values to file the table by.' : undefined
                            }
                          >
                            <button
                              type="button"
                              role="menuitemradio"
                              aria-checked={chosen}
                              id={`${module}-facet-nav-${facet.key}`}
                              disabled={empty}
                              onClick={() => {
                                onNavFacetChange?.(facet.key);

                                // Single choice, so the menu has nothing left to ask.
                                setSettingsOpen(false);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                            >
                              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                                {chosen ? (
                                  <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                                ) : null}
                              </span>
                              <span className="min-w-0 flex-1 truncate">{facet.label}</span>
                            </button>
                          </Tooltip>
                        );
                      })}
                    </>
                  ) : null}

                  {/* A section owns the divider above it — the index cog's rule. */}
                  <div className="my-1.5 border-t border-slate-200" role="separator" />

                  <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                    Layout
                  </p>

                  <button
                    type="button"
                    role="menuitem"
                    id={`${module}-facet-reset-width`}
                    onClick={() => {
                      resetWidth();
                      setSettingsOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
                  >
                    <span className="h-4 w-4 shrink-0" />
                    Reset width
                  </button>
                </div>
              ) : null}
            </div>
          </SearchGroup>
          </div>

          <Tooltip label="Close filters">
            <button
              type="button"
              id={`${module}-facet-close`}
              onClick={onClose}
              aria-label="Close filters"
              /*
                SQUARE, and sized to the group beside it rather than to its glyph — `self-stretch`
                takes the row's own height, so the two read as a pair rather than as a control
                with something small stuck beside it.
              */
              className="flex shrink-0 items-center self-stretch rounded-lg border border-slate-300 bg-white px-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
        </div>

        <div className="px-3 py-3">
          {/*
            A search that matches nothing says so. A rail that simply emptied would read as
            a rail that failed to load — the same reason an empty facet renders nothing
            rather than a heading over a blank list.
          */}
          {term !== '' && shownTotal === 0 && !loading ? (
            <p className="px-1 py-2 text-sm text-slate-500">No filter matches “{search.trim()}”.</p>
          ) : null}

          {drawn.map((facet) => {
            const values = counts[facet.key] ?? [];
            const chosen = selected[facet.key] ?? [];

            /*
              A facet with nothing in it renders nothing at all — not a label over an empty
              list. A facet's values come from the rows, so an empty one says there is nothing
              on that axis to filter by, and a bare heading reads as a control that failed to
              load. Skeletons still show while it is loading.
            */
            if (values.length === 0 && !loading) {
              return null;
            }

            /*
              `shown` is for RENDERING ONLY. Every piece of selection logic below still reads
              `values`, the full list, and that separation is load-bearing: `toggle` computes
              "every value except this one" and normalises to All at `values.length`, so
              handing it the filtered list would silently drop the reader's ticks on values the
              search happens to be hiding.
            */
            const shown = values.filter(matches);

            if (shown.length === 0 && term !== '') {
              return null;
            }

            /*
              Three states, not two.

              The axis is UNCONSTRAINED when the reader has said nothing about it — the key is
              absent — or when every offered value is ticked, which is the same result set.

              It EXCLUDES EVERYTHING when the key is present with nothing on it. That is a real
              filter, and the most restrictive one: no row qualifies. It used to be
              unreachable, because an empty selection was read as no selection.
            */
            const excludesEverything = facet.key in selected && chosen.length === 0;
            const unconstrained =
              !excludesEverything &&
              (chosen.length === 0 || values.every((value) => chosen.includes(value.value)));

            // Sentinel-spelled, so it cannot collide with a facet whose own value is "all".
            const allId = `${module}-facet-${facet.key}-__all__`;

            /*
              With All ticked, EVERY box in the group is ticked. All means every value, so a
              group showing one tick against four empty boxes contradicts itself — and it is
              the reading everyone has of a master checkbox. With the axis narrowed to nothing,
              every box is empty, which is the same reading run the other way.

              The ticks are DERIVED, not stored: the wire carries an absent parameter for All,
              not a list naming every row it does not exclude.
            */
            const ticked = (value: string) => unconstrained || chosen.includes(value);

            /*
              Unticking one value out of the all-ticked state selects every OTHER value —
              exactly what the boxes then show.

              Unticking the LAST remaining one now lands on "nothing selected" and returns no
              rows, rather than springing back to All. That spring-back was the thing that made
              the group impossible to clear: every route out of a narrowed axis led to every
              value being ticked again.

              Ticking the last missing one still normalises to All, because every value selected
              and no constraint ARE the same result set, and the group must not show two
              different answers for one list.
            */
            const toggle = (value: string) => {
              const effective = unconstrained ? values.map((v) => v.value) : chosen;

              const next = effective.includes(value)
                ? effective.filter((v) => v !== value)
                : [...effective, value];

              if (next.length === values.length) {
                onClear(facet.key);
                return;
              }

              onSelect(facet.key, next);
            };

            /*
              All is a master toggle in both directions: ticking it drops the axis, unticking it
              clears every value. The second direction is the one that was missing — clicking a
              ticked All was a no-op, so there was no gesture for "none of these".
            */
            const toggleAll = () => {
              if (unconstrained) {
                onSelect(facet.key, []);
                return;
              }

              onClear(facet.key);
            };

            return (
              <section key={facet.key} className="mb-4">
                <h2 className="px-1 pb-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                  {facet.label}
                </h2>

                <ul className="flex flex-col gap-0.5">
                  {values.length === 0 && loading
                    ? Array.from({ length: 2 }, (_, i) => (
                        <li key={i} className="px-2 py-1.5">
                          <Skeleton className="h-3.5 w-full" />
                        </li>
                      ))
                    : null}

                  {/*
                    All carries no count, and that is deliberate: the only honest number
                    there is the count with this axis unconstrained, and summing the values
                    below equals that only where they partition the list. They do on status
                    and unit; they do not on vacancy, where a post with no slots is in
                    neither bucket. A number that is right on two axes and wrong on the third
                    is worse than none.

                    Rendered only once there is something to be the alternative to — a lone
                    All over an empty facet is a control that does nothing.
                  */}
                  {values.length > 0 ? (
                    <li>
                      <label htmlFor={allId} className={FACET_ROW}>
                        <Checkbox
                          id={allId}
                          name={allId}
                          checked={unconstrained}
                          onChange={toggleAll}
                        />
                        <span className="min-w-0 flex-1 truncate">All</span>
                      </label>
                    </li>
                  ) : null}

                  {shown.map((value) => {
                    const inputId = `${module}-facet-${facet.key}-${value.value}`;

                    return (
                      <li key={value.value}>
                        <label htmlFor={inputId} className={FACET_ROW}>
                          <Checkbox
                            id={inputId}
                            name={inputId}
                            checked={ticked(value.value)}
                            onChange={() => toggle(value.value)}
                          />
                          <span className="min-w-0 flex-1 truncate">{value.label}</span>
                          <span className="shrink-0 font-mono tabular-nums text-xs text-slate-400">
                            {loading ? <Skeleton className="h-3 w-5" /> : value.count}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                  </ul>
                </section>
              );
          })}
        </div>
      </aside>

      {/*
        The resize handle, on the rail's trailing edge.

        A button rather than a bare div: it is a control, so it is focusable and named. It
        keeps `col-resize` through a `cursor-*` utility, which beats the base rule that would
        otherwise give every button a hand — the same opt-out the table's column handles take.
      */}
      <button
        type="button"
        id={`${module}-filter-rail-resize`}
        aria-label="Resize the filter rail"
        onPointerDown={startResize}
        onDoubleClick={resetWidth}
        tabIndex={open ? 0 : -1}
        className="group absolute right-0 top-0 h-full w-2 cursor-col-resize bg-transparent transition hover:bg-slate-200 focus:outline-none focus-visible:bg-blue-100"
      >
        {/*
          The standing indicator that the border can be dragged, centred on it.

          It is drawn AT REST, not on hover — a resize affordance nobody knows to look for is
          one nobody uses, and the edge gives no other clue. That is the opposite call from
          the scrollbar, and for the opposite reason: a scrollbar reports something the reader
          can already see, where this offers something they cannot otherwise discover.

          `pointer-events-none`, so the 8px button behind it takes every click.

          Flush against the rail's inner edge rather than centred ON the border: the wrapper
          is `overflow-hidden` — it has to be, that is what clips the content while the rail
          collapses — so anything overhanging would be sliced down its right side.
        */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-1/2 flex h-7 w-3 -translate-y-1/2 items-center justify-center rounded-l-full border border-r-0 border-slate-300 bg-white shadow-sm transition group-hover:border-slate-400"
        >
          <GripVertical
            className="h-3 w-3 text-slate-400 transition group-hover:text-slate-600"
            aria-hidden="true"
          />
        </span>
      </button>
    </div>
  );
}
